import { supabase } from '../lib/supabase'

// Check if current user is a super admin
export const checkIsSuperAdmin = async () => {
  try {
    console.log('🔐 adminService: Getting current user...')
    const { data: { user } } = await supabase.auth.getUser()
    
    if (!user) {
      console.log('🔐 adminService: No user found')
      return false
    }

    console.log('🔐 adminService: User found:', user.id, 'calling RPC...')
    
    // Use the RPC function to check admin status
    const { data, error } = await supabase
      .rpc('check_is_super_admin')

    console.log('🔐 adminService: RPC result:', { data, error })

    if (error) {
      console.error('🔐 adminService: Error checking super admin status:', error)
      return false
    }

    const isAdmin = data === true
    console.log('🔐 adminService: Final admin status:', isAdmin)
    return isAdmin
  } catch (error) {
    console.error('🔐 adminService: Exception checking super admin status:', error)
    return false
  }
}

// Get admin statistics
export const getAdminStatistics = async () => {
  try {
    const { data, error } = await supabase
      .rpc('get_admin_statistics')

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error fetching admin statistics:', error)
    return { data: null, error }
  }
}

// Get all users with pagination
export const getAllUsers = async (page = 1, limit = 50, searchTerm = '') => {
  try {
    // Use RPC function to get all users (bypasses RLS)
    const { data: allUsers, error: usersError } = await supabase
      .rpc('get_all_users_admin')

    if (usersError) throw usersError

    // Filter by search term if provided
    let filteredUsers = allUsers || []
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      filteredUsers = filteredUsers.filter(user => 
        user.email?.toLowerCase().includes(searchLower) ||
        user.raw_user_meta_data?.full_name?.toLowerCase().includes(searchLower) ||
        user.raw_user_meta_data?.display_name?.toLowerCase().includes(searchLower)
      )
    }

    // Sort by created_at descending
    filteredUsers.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

    // Paginate
    const from = (page - 1) * limit
    const to = from + limit
    const paginatedUsers = filteredUsers.slice(from, to)
    const count = filteredUsers.length

    // Also get group membership counts for each user
    const userIds = paginatedUsers.map(u => u.id)
    let membershipMap = {}
    
    if (userIds.length > 0) {
      const { data: membershipCounts } = await supabase
        .from('group_members')
        .select('user_id')
        .in('user_id', userIds)

      // Count memberships per user
      membershipCounts?.forEach(m => {
        membershipMap[m.user_id] = (membershipMap[m.user_id] || 0) + 1
      })
    }

    // Add membership count to users
    const enrichedUsers = paginatedUsers.map(user => ({
      ...user,
      group_count: membershipMap[user.id] || 0,
      display_name: user.raw_user_meta_data?.full_name || user.raw_user_meta_data?.display_name || user.email?.split('@')[0]
    }))

    return { 
      data: enrichedUsers, 
      count,
      error: null 
    }
  } catch (error) {
    console.error('Error fetching all users:', error)
    return { data: null, count: 0, error }
  }
}

// Get all groups with pagination
export const getAllGroups = async (page = 1, limit = 50, searchTerm = '') => {
  try {
    // Use RPC function to get all groups (bypasses RLS)
    const { data: allGroups, error: groupsError } = await supabase
      .rpc('get_all_groups_admin')

    if (groupsError) throw groupsError

    // Filter by search term if provided
    let filteredGroups = allGroups || []
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      filteredGroups = filteredGroups.filter(group => 
        group.name?.toLowerCase().includes(searchLower)
      )
    }

    // Sort by created_at descending
    filteredGroups.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

    // Paginate
    const from = (page - 1) * limit
    const to = from + limit
    const paginatedGroups = filteredGroups.slice(from, to)
    const count = filteredGroups.length

    // Get member counts for these groups
    const groupIds = paginatedGroups.map(g => g.id)
    let memberCounts = {}
    let roundCounts = {}
    let creatorInfo = {}

    if (groupIds.length > 0) {
      // Get member counts
      const { data: memberCountData } = await supabase
        .from('group_members')
        .select('group_id')
        .in('group_id', groupIds)

      memberCountData?.forEach(m => {
        memberCounts[m.group_id] = (memberCounts[m.group_id] || 0) + 1
      })

      // Get round counts using RPC
      const { data: allRounds } = await supabase
        .rpc('get_all_rounds_admin')

      allRounds?.forEach(r => {
        if (groupIds.includes(r.group_id)) {
          roundCounts[r.group_id] = (roundCounts[r.group_id] || 0) + 1
        }
      })

      // Get creator info using RPC to bypass RLS
      const { data: creatorData } = await supabase
        .rpc('get_all_users_admin')

      if (creatorData) {
        creatorData.forEach(user => {
          creatorInfo[user.id] = user.raw_user_meta_data?.full_name || user.email?.split('@')[0] || 'Unknown'
        })
      }
    }

    // Format the data
    const formattedGroups = paginatedGroups.map(group => ({
      ...group,
      member_count: memberCounts[group.id] || 0,
      round_count: roundCounts[group.id] || 0,
      creator_name: creatorInfo[group.created_by] || 'Unknown'
    }))

    return { 
      data: formattedGroups, 
      count,
      error: null 
    }
  } catch (error) {
    console.error('Error fetching all groups:', error)
    return { data: null, count: 0, error }
  }
}

// Get all rounds with pagination
export const getAllRounds = async (page = 1, limit = 50, filters = {}) => {
  try {
    // Use RPC function to get all rounds (bypasses RLS)
    const { data: allRounds, error: roundsError } = await supabase
      .rpc('get_all_rounds_admin')

    if (roundsError) throw roundsError

    // Apply filters
    let filteredRounds = allRounds || []
    
    if (filters.groupId) {
      filteredRounds = filteredRounds.filter(r => r.group_id === filters.groupId)
    }
    
    const today = new Date().toISOString().split('T')[0]
    if (filters.status === 'upcoming') {
      filteredRounds = filteredRounds.filter(r => r.date >= today)
    } else if (filters.status === 'past') {
      filteredRounds = filteredRounds.filter(r => r.date < today)
    }

    // Sort by date descending
    filteredRounds.sort((a, b) => new Date(b.date) - new Date(a.date))

    // Paginate
    const from = (page - 1) * limit
    const to = from + limit
    const paginatedRounds = filteredRounds.slice(from, to)
    const count = filteredRounds.length

    // Get additional data for these rounds
    const roundIds = paginatedRounds.map(r => r.id)
    const groupIds = [...new Set(paginatedRounds.map(r => r.group_id))]
    let invitationCounts = {}
    let groupNames = {}
    let creatorInfo = {}

    if (roundIds.length > 0) {
      // Get invitation counts
      const { data: invitationData } = await supabase
        .from('round_invitations')
        .select('round_id')
        .in('round_id', roundIds)

      invitationData?.forEach(i => {
        invitationCounts[i.round_id] = (invitationCounts[i.round_id] || 0) + 1
      })

      // Get group names using RPC
      const { data: allGroups } = await supabase
        .rpc('get_all_groups_admin')

      allGroups?.forEach(g => {
        if (groupIds.includes(g.id)) {
          groupNames[g.id] = g.name
        }
      })

      // Get creator info using RPC to bypass RLS
      const { data: creatorData } = await supabase
        .rpc('get_all_users_admin')

      if (creatorData) {
        creatorData.forEach(user => {
          creatorInfo[user.id] = user.raw_user_meta_data?.full_name || user.email?.split('@')[0] || 'Unknown'
        })
      }
    }

    // Format the data
    const formattedRounds = paginatedRounds.map(round => {
      // Combine date and time for comparison and display
      const roundDateTime = round.date && round.time ? 
        new Date(`${round.date}T${round.time}`) : 
        new Date(round.date)
      
      return {
        ...round,
        date_time: roundDateTime.toISOString(), // Add combined datetime for sorting
        group_name: groupNames[round.group_id] || 'Unknown Group',
        invitation_count: invitationCounts[round.id] || 0,
        creator_name: creatorInfo[round.created_by] || 'Unknown',
        is_upcoming: roundDateTime > new Date()
      }
    })

    return { 
      data: formattedRounds, 
      count,
      error: null 
    }
  } catch (error) {
    console.error('Error fetching all rounds:', error)
    return { data: null, count: 0, error }
  }
}

// Get recent activity (for dashboard)
export const getRecentActivity = async (limit = 20) => {
  try {
    // Get recent rounds
    const { data: recentRounds } = await supabase
      .from('rounds')
      .select(`
        id,
        title,
        date_time,
        created_at,
        golf_groups!rounds_group_id_fkey(name)
      `)
      .order('created_at', { ascending: false })
      .limit(limit / 2)

    // Get recent group joins
    const { data: recentJoins } = await supabase
      .from('group_members')
      .select(`
        id,
        created_at,
        member_name,
        golf_groups(name)
      `)
      .order('created_at', { ascending: false })
      .limit(limit / 2)

    // Combine and sort by date
    const activities = [
      ...(recentRounds || []).map(r => ({
        type: 'round_created',
        description: `New round "${r.title}" scheduled in ${r.golf_groups?.name}`,
        date: r.created_at,
        metadata: r
      })),
      ...(recentJoins || []).map(j => ({
        type: 'member_joined',
        description: `${j.member_name} joined ${j.golf_groups?.name}`,
        date: j.created_at,
        metadata: j
      }))
    ].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, limit)

    return { data: activities, error: null }
  } catch (error) {
    console.error('Error fetching recent activity:', error)
    return { data: null, error }
  }
}

// Export data to CSV
export const exportToCSV = (data, filename) => {
  if (!data || data.length === 0) return

  // Get headers from first object
  const headers = Object.keys(data[0])
  
  // Create CSV content
  const csvContent = [
    headers.join(','),
    ...data.map(row => 
      headers.map(header => {
        const value = row[header]
        // Handle nested objects and arrays
        if (typeof value === 'object' && value !== null) {
          return JSON.stringify(value).replace(/,/g, ';')
        }
        // Escape commas and quotes in strings
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`
        }
        return value ?? ''
      }).join(',')
    )
  ].join('\n')

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`)
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

// Get all custom courses with pagination
export const getAllCourses = async (page = 1, limit = 50, searchTerm = '') => {
  try {
    // Use RPC function to get all custom courses (bypasses RLS)
    const { data: allCourses, error: coursesError } = await supabase
      .rpc('get_all_custom_courses_admin')

    if (coursesError) throw coursesError

    // Filter by search term if provided
    let filteredCourses = allCourses || []
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      filteredCourses = filteredCourses.filter(course => 
        course.name?.toLowerCase().includes(searchLower)
      )
    }

    // Sort by created_at descending
    filteredCourses.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

    // Paginate
    const from = (page - 1) * limit
    const to = from + limit
    const paginatedCourses = filteredCourses.slice(from, to)
    const count = filteredCourses.length

    // Get usage data for these courses (rounds that reference this course by name)
    const courseNames = paginatedCourses.map(c => c.name)
    let usageCounts = {}
    let creatorInfo = {}

    if (courseNames.length > 0) {
      // Get usage counts using RPC to get all rounds
      const { data: allRounds } = await supabase
        .rpc('get_all_rounds_admin')

      allRounds?.forEach(r => {
        if (courseNames.includes(r.course)) {
          usageCounts[r.course] = (usageCounts[r.course] || 0) + 1
        }
      })

      // Get creator info using RPC to bypass RLS
      const { data: creatorData } = await supabase
        .rpc('get_all_users_admin')

      if (creatorData) {
        creatorData.forEach(user => {
          creatorInfo[user.id] = user.raw_user_meta_data?.full_name || user.email?.split('@')[0] || 'Unknown'
        })
      }
    }

    // Format the data
    const formattedCourses = paginatedCourses.map(course => ({
      ...course,
      usage_count: usageCounts[course.name] || 0,
      creator_name: creatorInfo[course.user_id] || 'Unknown',
      is_custom: true // All courses in this table are custom
    }))

    return { 
      data: formattedCourses, 
      count,
      error: null 
    }
  } catch (error) {
    console.error('Error fetching all courses:', error)
    return { data: null, count: 0, error }
  }
}

// Add a user as super admin (only accessible by existing super admins)
export const addSuperAdmin = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('super_admins')
      .insert([{ user_id: userId }])
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error adding super admin:', error)
    return { data: null, error }
  }
}

// Remove a user as super admin
export const removeSuperAdmin = async (userId) => {
  try {
    const { error } = await supabase
      .from('super_admins')
      .delete()
      .eq('user_id', userId)

    if (error) throw error
    return { error: null }
  } catch (error) {
    console.error('Error removing super admin:', error)
    return { error }
  }
}

// Get landing page metrics
export const getLandingPageMetrics = async () => {
  try {
    const { data, error } = await supabase
      .rpc('get_landing_page_metrics')

    if (error) throw error

    // Return first row or defaults
    const metrics = data?.[0] || {
      rounds_coordinated: 1000,
      happy_golfers: 500,
      active_groups: 50,
      show_social_proof: true,
      beta_users_count: 0,
      beta_total_slots: 500,
      updated_at: null,
      updated_by: null
    }

    return { data: metrics, error: null }
  } catch (error) {
    console.error('Error fetching landing page metrics:', error)
    return {
      data: {
        rounds_coordinated: 1000,
        happy_golfers: 500,
        active_groups: 50,
        show_social_proof: true,
        beta_users_count: 0,
        beta_total_slots: 500,
        updated_at: null,
        updated_by: null
      },
      error
    }
  }
}

// Update landing page metrics (super admins only)
export const updateLandingPageMetrics = async (roundsCoordinated, happyGolfers, activeGroups, showSocialProof = true, betaUsersCount = 0, betaTotalSlots = 500) => {
  try {
    const { data, error } = await supabase
      .rpc('update_landing_page_metrics', {
        p_rounds_coordinated: roundsCoordinated,
        p_happy_golfers: happyGolfers,
        p_active_groups: activeGroups,
        p_show_social_proof: showSocialProof,
        p_beta_users_count: betaUsersCount,
        p_beta_total_slots: betaTotalSlots
      })

    if (error) throw error
    return { data: data?.[0], error: null }
  } catch (error) {
    console.error('Error updating landing page metrics:', error)
    return { data: null, error }
  }
}

// ============================================================
// API RATE LIMITING ADMIN FUNCTIONS
// ============================================================

// Get default rate limits
export const getDefaultRateLimits = async () => {
  try {
    const { data, error } = await supabase.rpc('get_default_rate_limits')
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error fetching default rate limits:', error)
    return { data: null, error }
  }
}

// Update default rate limits
export const updateDefaultRateLimits = async (limits) => {
  try {
    const { data, error } = await supabase.rpc('update_default_rate_limits', {
      p_limit_per_minute: limits.limit_per_minute,
      p_limit_per_hour: limits.limit_per_hour,
      p_limit_per_day: limits.limit_per_day,
      p_limit_per_week: limits.limit_per_week
    })
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error updating default rate limits:', error)
    return { data: null, error }
  }
}

// Get users with custom rate limits
export const getUsersWithCustomLimits = async () => {
  try {
    const { data, error } = await supabase.rpc('get_users_with_custom_limits')
    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('Error fetching users with custom limits:', error)
    return { data: [], error }
  }
}

// Get rate limits for a specific user
export const getUserRateLimits = async (userId) => {
  try {
    const { data, error } = await supabase.rpc('get_user_rate_limits', {
      p_user_id: userId
    })
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error fetching user rate limits:', error)
    return { data: null, error }
  }
}

// Set custom rate limits for a specific user
export const setUserRateLimits = async (userId, limits) => {
  try {
    const { data, error } = await supabase.rpc('set_user_rate_limits', {
      p_user_id: userId,
      p_limit_per_minute: limits.limit_per_minute,
      p_limit_per_hour: limits.limit_per_hour,
      p_limit_per_day: limits.limit_per_day,
      p_limit_per_week: limits.limit_per_week
    })
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error setting user rate limits:', error)
    return { data: null, error }
  }
}

// Delete custom rate limits for a user (revert to defaults)
export const deleteUserRateLimits = async (userId) => {
  try {
    const { data, error } = await supabase.rpc('delete_user_rate_limits', {
      p_user_id: userId
    })
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error deleting user rate limits:', error)
    return { data: null, error }
  }
}

// Get API usage summary (aggregate stats)
export const getApiUsageSummary = async (timeRange = '7d') => {
  try {
    const { data, error } = await supabase.rpc('get_api_usage_summary', {
      p_time_range: timeRange
    })
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error fetching API usage summary:', error)
    return { data: null, error }
  }
}

// Get top API users
export const getTopApiUsers = async (limit = 10, timeRange = '7d') => {
  try {
    const { data, error } = await supabase.rpc('get_top_api_users', {
      p_limit: limit,
      p_time_range: timeRange
    })
    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('Error fetching top API users:', error)
    return { data: [], error }
  }
}

// Get detailed API usage for a specific user
export const getUserApiUsageDetail = async (userId, timeRange = '7d') => {
  try {
    const { data, error } = await supabase.rpc('get_user_api_usage_detail', {
      p_user_id: userId,
      p_time_range: timeRange
    })
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error fetching user API usage detail:', error)
    return { data: null, error }
  }
}

// --- GolfNow facility-id overrides (user suggestions awaiting admin review) ---

// List pending per-user facility-id overrides for review.
export const getPendingFacilityOverrides = async (limit = 50) => {
  try {
    const { data, error } = await supabase.rpc('get_pending_facility_overrides', {
      p_limit_count: limit
    })
    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('Error fetching pending facility overrides:', error)
    return { data: [], error }
  }
}

// Promote an override into the shared golf_clubs row.
export const applyFacilityOverride = async (overrideId) => {
  try {
    const { data, error } = await supabase.rpc('apply_facility_override', {
      p_override_id: overrideId
    })
    if (error) throw error
    if (data && data.success === false) throw new Error(data.error || 'Failed to apply override')
    return { data, error: null }
  } catch (error) {
    console.error('Error applying facility override:', error)
    return { data: null, error }
  }
}

// Reject an override without touching golf_clubs.
export const rejectFacilityOverride = async (overrideId) => {
  try {
    const { data, error } = await supabase.rpc('reject_facility_override', {
      p_override_id: overrideId
    })
    if (error) throw error
    if (data && data.success === false) throw new Error(data.error || 'Failed to reject override')
    return { data, error: null }
  } catch (error) {
    console.error('Error rejecting facility override:', error)
    return { data: null, error }
  }
}


